import { Container, Box, Typography, Paper, Breadcrumbs, Link, Button } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Hero from '../components/common/Hero';
import HomeIcon from '@mui/icons-material/Home';
import GavelIcon from '@mui/icons-material/Gavel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const Terms = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Terms of Use | Brynleigh Estates HOA</title>
        <meta name="description" content="Terms of Use for the Brynleigh Estates HOA website. Review the terms and conditions for using our website and services." />
        <link rel="canonical" href="https://brynleighestates.com/terms" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://brynleighestates.com/terms" />
        <meta property="og:title" content="Terms of Use | Brynleigh Estates HOA" />
        <meta property="og:description" content="Terms of Use for the Brynleigh Estates HOA website. Review the terms and conditions for using our website and services." />
        <meta property="og:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Terms of Use | Brynleigh Estates HOA" />
        <meta name="twitter:description" content="Terms of Use for the Brynleigh Estates HOA website." />
      </Helmet>
      
      <Hero 
        imageUrl="/images/hills-bg.jpeg"
        title="Terms of Use"
        subtitle="Terms and conditions for using our website"
        height="small"
      />
      
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
            <Link 
              underline="hover" 
              color="inherit" 
              component={RouterLink}
              to="/" 
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Home
            </Link>
            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
              <GavelIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Terms of Use
            </Typography>
          </Breadcrumbs>
          
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate(-1)}
            sx={{ mb: 4 }}
          >
            Go Back
          </Button>

          <Paper elevation={0} sx={{ p: 4, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Terms of Use
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              Last updated: January 2026
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Acceptance of Terms
            </Typography>
            <Typography variant="body1" paragraph>
              By accessing and using the Brynleigh Estates Homeowners Association website ("Site"), you accept and agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use this Site.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Use of the Site
            </Typography>
            <Typography variant="body1" paragraph>
              This Site is intended for use by residents, homeowners, and prospective residents of Brynleigh Estates. You agree to use this Site only for lawful purposes and in accordance with these Terms.
            </Typography>
            <Typography variant="body1" paragraph>
              You agree NOT to:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">Use the Site in any way that violates applicable laws or regulations</Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">Attempt to gain unauthorized access to any portion of the Site</Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">Submit false or misleading information through any forms</Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">Use the Site to transmit spam, malware, or other harmful content</Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">Interfere with or disrupt the Site or servers connected to the Site</Typography>
              </Box>
            </Box>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Content and Information
            </Typography>
            <Typography variant="body1" paragraph>
              The content on this Site is provided for general information purposes. While we strive to keep information accurate and up-to-date, we make no warranties about the completeness, reliability, or accuracy of this information.
            </Typography>
            <Typography variant="body1" paragraph>
              Official HOA documents, including covenants, bylaws, and meeting minutes, are provided as reference. In case of any discrepancy, the official recorded documents shall prevail.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              User Submissions
            </Typography>
            <Typography variant="body1" paragraph>
              When you submit information through forms on this Site (such as contact forms, ARC requests, or contact updates), you represent that:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">The information you provide is accurate and complete</Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">You have the right to submit such information</Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body1">Your submission does not violate any third-party rights</Typography>
              </Box>
            </Box>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Intellectual Property
            </Typography>
            <Typography variant="body1" paragraph>
              All content on this Site, including text, graphics, logos, and images, is the property of Brynleigh Estates HOA or its content suppliers and is protected by applicable intellectual property laws.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Third-Party Links
            </Typography>
            <Typography variant="body1" paragraph>
              This Site may contain links to third-party websites for your convenience. We do not control and are not responsible for the content or practices of any linked third-party sites. The inclusion of any link does not imply endorsement by Brynleigh Estates HOA.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Disclaimer of Warranties
            </Typography>
            <Typography variant="body1" paragraph>
              THIS SITE IS PROVIDED "AS IS" WITHOUT ANY WARRANTIES, EXPRESS OR IMPLIED. BRYNLEIGH ESTATES HOA DISCLAIMS ALL WARRANTIES, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Limitation of Liability
            </Typography>
            <Typography variant="body1" paragraph>
              Brynleigh Estates HOA shall not be liable for any damages arising from your use of or inability to use this Site, including direct, indirect, incidental, or consequential damages.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Governing Law
            </Typography>
            <Typography variant="body1" paragraph>
              These Terms shall be governed by and construed in accordance with the laws of the State of Alabama, without regard to its conflict of law provisions.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Changes to Terms
            </Typography>
            <Typography variant="body1" paragraph>
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to the Site. Your continued use of the Site after changes are posted constitutes acceptance of the modified Terms.
            </Typography>

            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Contact Information
            </Typography>
            <Typography variant="body1" paragraph>
              If you have questions about these Terms of Use, please contact us at:
            </Typography>
            <Typography variant="body1">
              Email: <Link href="mailto:board@brynleighestates.com">board@brynleighestates.com</Link>
            </Typography>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default Terms;
